import {
    ImageContainerDimensionsRestDto,
    ImagePositioningRestDto,
    ImageRestDto,
    ImageRoundingRestDto,
    MultilingualFieldRestDto,
    OverlayRestDto
} from "@/api/pharma-cms-content-block/models";
import {MultilingualRestDtoModel} from "@/models/MultilingualRestDtoModel";
import {ImageTargetEnum} from "@/api/enums/image-target-enum";
import {BorderWidthEnum} from "@/api/enums/border-width-enum";
import {BorderTypeEnum} from "@/api/enums/border-type-enum";
import {LightboxLayoutEnum} from "@/api/enums/lightbox-layout-enum";
import {HorizontalImageShiftEnum} from "@/api/enums/horizontal-image-shift-enum";
import {VerticalImageShiftEnum} from "@/api/enums/vertical-image-shift-enum";
import {ImageRoundingRestDtoModel} from "@/models/api/pharma-cms-content-block/ImageRoundingRestDtoModel";
import {
    ImageContainerDimensionsRestDtoModel
} from "@/models/api/pharma-cms-content-block/ImageContainerDimensionsRestDtoModel";
import {ImagePositioningRestDtoModel} from "@/models/api/pharma-cms-content-block/ImagePositioningRestDtoModel";
import {OverlayRestDtoModel} from "@/models/api/pharma-cms-content-block/OverlayRestDtoModel";

export class ImageRestDtoModel implements ImageRestDto {
    border_type_code: BorderTypeEnum;
    border_width_code?: BorderWidthEnum;
    container_dimensions?: ImageContainerDimensionsRestDto | null;
    horizontal_image_shift_code?: HorizontalImageShiftEnum | null;
    image_key: MultilingualRestDtoModel;
    image_page_id?: MultilingualRestDtoModel;
    image_target?: ImageTargetEnum;
    image_url?: MultilingualRestDtoModel;
    lightbox_layout_code: LightboxLayoutEnum;
    overlays?: OverlayRestDto[] | null;
    positioning?: ImagePositioningRestDto | null;
    rounding?: ImageRoundingRestDto | null;
    title?: MultilingualRestDtoModel;
    vertical_image_shift_code?: VerticalImageShiftEnum | null;

    constructor(border_type_code: BorderTypeEnum, image_key: MultilingualRestDtoModel, lightbox_layout_code: LightboxLayoutEnum) {
        this.border_type_code = border_type_code;
        this.image_key = image_key;
        this.lightbox_layout_code = lightbox_layout_code;
    }

    public static createWithDefaults(): ImageRestDtoModel {
        const model = new ImageRestDtoModel(
            BorderTypeEnum.NONE,
            MultilingualRestDtoModel.createWithDefaults(),
            LightboxLayoutEnum.NONE
        );

        model.image_key = MultilingualRestDtoModel.createWithDefaults();
        model.title = MultilingualRestDtoModel.createWithDefaults();
        model.horizontal_image_shift_code = HorizontalImageShiftEnum.NO_SHIFT;
        model.vertical_image_shift_code = VerticalImageShiftEnum.NO_SHIFT;

        return model;
    }

    public static cloneFrom(source: ImageRestDto): ImageRestDtoModel {
        const clone: ImageRestDtoModel = new this(
            // image_key, lightbox_layout_code and border_type_code are the three required fields of
            // ImageRestDto, but the API drops them when their column is NULL on the content block
            // (border_type_code is left out of the response entirely) while still demanding them
            // back on save. Every one of them therefore falls back to what createWithDefaults()
            // would have used, so a loaded block can always be saved again.
            source.border_type_code ?? BorderTypeEnum.NONE,
            ImageRestDtoModel.cloneMultilingualField(source.image_key) ?? MultilingualRestDtoModel.createWithDefaults(),
            source.lightbox_layout_code ?? LightboxLayoutEnum.NONE
        );

        clone.border_width_code = source.border_width_code ?? undefined;
        clone.image_target = source.image_target ?? undefined;
        clone.horizontal_image_shift_code = source.horizontal_image_shift_code;
        clone.vertical_image_shift_code = source.vertical_image_shift_code;
        clone.title = ImageRestDtoModel.cloneMultilingualField(source.title);
        clone.image_url = ImageRestDtoModel.cloneMultilingualField(source.image_url);
        clone.image_page_id = ImageRestDtoModel.cloneMultilingualField(source.image_page_id);

        if (source.rounding) {
            clone.rounding = ImageRoundingRestDtoModel.cloneFrom(source.rounding);
        }
        if (source.container_dimensions) {
            clone.container_dimensions = ImageContainerDimensionsRestDtoModel.cloneFrom(source.container_dimensions);
        }
        if (source.positioning) {
            clone.positioning = ImagePositioningRestDtoModel.cloneFrom(source.positioning);
        }
        if (source.overlays) {
            // cloneImageRestDtoMetaData() copies overlays by reference, so the
            // metadata clipboard would alias the source without this deep clone.
            clone.overlays = source.overlays.map((overlay: OverlayRestDto) => OverlayRestDtoModel.cloneFrom(overlay));
        }

        return clone;
    }

    private static cloneMultilingualField(source?: MultilingualFieldRestDto | null): MultilingualRestDtoModel | undefined {
        if (!source) {
            return undefined;
        }

        return new MultilingualRestDtoModel(source.de ?? null, source.en ?? null, source.fr ?? null, source.nl ?? null);
    }
}
